<template>
  <!-- Add animated background -->
  <div class="animated-background">
    <div class="floating-elements">
      <span class="element code">{ }</span>
      <span class="element book">📚</span>
      <span class="element graduation">🎓</span>
      <span class="element tech">💻</span>
      <span class="element atom">⚛️</span>
      <span class="element formula">∑</span>
      <span class="element infinity">∞</span>
      <span class="element binary">01</span>
      <span class="element circuit"></span>
      <span class="element grid"></span>
    </div>
  </div>
  <div dir="rtl">
    <!-- First row: Page title -->
    <div class="mb-4">
      <h1>إدارة المستخدمين</h1>
    </div>
    
    <!-- Second row: Add button and search bar -->
    <div class="d-flex mb-4">
      <div style="width: 25%; padding-left: 16px;">
        <v-btn 
          color="primary" 
          @click="showAddDialog = true" 
          block
          height="56px"
          class="mt-n1"
        >
          <v-icon start>mdi-plus</v-icon>
          إضافة مستخدم
        </v-btn>
      </div>
      <div style="width: 75%;">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث"
          single-line
          hide-details
          class="rtl-input"
        ></v-text-field>
      </div>
    </div>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="users"
      :search="search"
      class="elevation-1"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td class="text-center">{{ item.name }}</td>
          <td class="text-center">{{ item.username }}</td>
          <td class="text-center">{{ item.phone }}</td>
          <td class="text-center">
            <v-icon
              :color="item.active ? 'green' : 'red'"
              @click="toggleUserStatus(item)"
            >
              mdi-power
            </v-icon>
          </td>
          <td class="text-center">
            <v-icon small class="mr-2" @click="editUser(item)" title="تعديل">mdi-pencil</v-icon>
           
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Add User Dialog -->
    <v-dialog v-model="showAddDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-right">إضافة مستخدم جديد</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newUser.name"
            label="الاسم"
            class="rtl-input"
          ></v-text-field>
          <v-text-field
            v-model="newUser.phone"
            label="رقم الهاتف"
            class="rtl-input"
          ></v-text-field>
          <v-text-field
            v-model="newUser.username"
            label="اسم المستخدم"
            class="rtl-input"
          ></v-text-field>
          <v-text-field
            v-model="newUser.password"
            label="كلمة المرور"
            type="password"
            class="rtl-input"
          ></v-text-field>
          <v-select
            v-model="newUser.roles_ids"
            :items="roles"
            item-title="name"
            item-value="id"
            label="الصلاحيات"
            multiple
            chips
            :loading="isLoadingRoles"
            persistent-hint
            hint="اختر صلاحيات المستخدم"
            class="rtl-input"
          ></v-select>
        </v-card-text>
        <v-card-actions class="rtl-dialog">
          <v-btn color="error" text @click="showAddDialog = false">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="addNewUser">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit User Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-right">تعديل المستخدم</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="currentUser.name"
            label="الاسم"
            class="rtl-input"
          ></v-text-field>
          <v-text-field
            v-model="currentUser.phone"
            label="رقم الهاتف"
            class="rtl-input"
          ></v-text-field>
          <v-text-field
            v-model="currentUser.username"
            label="اسم المستخدم"
            class="rtl-input"
          ></v-text-field>
          <v-text-field
            v-model="currentUser.password"
            label="كلمة المرور"
            type="password"
            class="rtl-input"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="rtl-dialog">
          <v-btn color="error" text @click="showEditDialog = false">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="updateUserDetails">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

export default {
  data() {
    return {
      search: '',
      isLoading: false,
      newUser: {
        name: '',
        phone: '',
        username: '',
        password: '',
        active: 1,
        roles_ids: []
      },
      currentUser: {},
      showAddDialog: false,
      showEditDialog: false,
      headers: [
        { title: 'الاسم', key: 'name', align: 'center' },
        { title: 'اسم المستخدم', key: 'username', align: 'center' },
        { title: 'رقم الهاتف', key: 'phone', align: 'center' },
        { title: 'الحالة', key: 'active', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      isLoadingRoles: true
    };
  },
  computed: {
    ...mapGetters(['allUsers', 'allRoles']),
    users() {
      return this.allUsers;
    },
    roles() {
      return this.allRoles || [];
    }
  },
  methods: {
    ...mapActions(['fetchUsers', 'addUser', 'updateUser', 'fetchRoles']),
    editUser(item) {
      this.currentUser = { ...item };
      this.showEditDialog = true;
    },
    async toggleUserStatus(item) {
      try {
        const user = { ...item };
        user.active = !user.active;
        await this.updateUser(user);
        await this.refreshTable();
      } catch (error) {
        console.error('خطأ في تحديث حالة المستخدم:', error);
      }
    },
    async loadRoles() {
      try {
        this.isLoadingRoles = true;
        await this.fetchRoles();
      } catch (error) {
        console.error('خطأ في تحميل الصلاحيات:', error);
      } finally {
        this.isLoadingRoles = false;
      }
    },
    async addNewUser() {
      try {
        this.newUser.roles_ids = Array.isArray(this.newUser.roles_ids) 
          ? this.newUser.roles_ids.map(role => role.id || role)
          : [];
          
        await this.addUser(this.newUser);
        this.showAddDialog = false;
        await this.refreshTable();
        
        this.newUser = {
          name: '',
          phone: '',
          username: '',
          password: '',
          active: 1,
          roles_ids: []
        };
      } catch (error) {
        console.error('خطأ في إضافة المستخدم:', error);
      }
    },
    async updateUserDetails() {
      try {
        await this.updateUser(this.currentUser);
        this.showEditDialog = false;
        await this.refreshTable();
      } catch (error) {
        console.error('خطأ في تحديث بيانات المستخدم:', error);
      }
    },
    // Updated search handler with more logging
    handleSearch: debounce(async function() {
      console.log('Search triggered with:', this.search); // Debug log
      
      try {
        this.isLoading = true;
        if (!this.search || !this.search.trim()) {
          console.log('Empty search - fetching all users'); // Debug log
          await this.fetchUsers();
        } else {
          const searchTerm = this.search.trim();
          console.log('Searching for:', searchTerm); // Debug log
          await this.fetchUsers({ name: searchTerm });
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        this.isLoading = false;
      }
    }, 300),
    // Updated refresh method with logging
    async refreshTable() {
      console.log('Refreshing table with search:', this.search); // Debug log
      
      try {
        this.isLoading = true;
        if (this.search && this.search.trim()) {
          await this.fetchUsers({ name: this.search.trim() });
        } else {
          await this.fetchUsers();
        }
      } catch (error) {
        console.error('خطأ في تحديث الجدول:', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  async created() {
    try {
      await this.loadRoles();
      await this.refreshTable();
    } catch (error) {
      console.error('خطأ في تهيئة الصفحة:', error);
    }
  },
  watch: {
    showAddDialog(val) {
      if (val && this.roles.length === 0) {
        this.loadRoles();
      }
    },
    // Add watcher for search
    search: {
      handler(newVal) {
        console.log('Search query changed:', newVal); // Debug log
        this.handleSearch();
      }
    }
  }
};
</script>

<!-- UserManagement styles are now handled globally in src/assets/styles/global.css -->